import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import Icon from '@amzn/meridian/icon';
import React from 'react';
import { TranslationFunction } from '../../components/blocks';
import { Link } from '../../components/blocks/localization';
import Button from '../../components/blocks/localization/Button';
import { TruncatedText } from '../../components/composites';
import { DateFilterField, ResellerItem, SalesOrderDemand, SalesOrderDemandItem, TableData } from '../../redux/types';
import { SiteMapPage } from '../../types';
import { formatDateIsoOrBlank } from '../../utils/dateUtils';
import { toFamilyTranslationKey } from '../../utils/textUtils';

export interface SalesOrderDemandRow extends SalesOrderDemand {
    item: SalesOrderDemandItem;
    itemCount: number;
    isSummary?: boolean;
}

export const calcTotals = (items: SalesOrderDemandItem[]) => ({
    ordered: items.reduce((sum, i) => sum + i.orderedQuantity, 0),
    open: items.reduce((sum, i) => sum + i.openQuantity, 0),
    released: items.reduce((sum, i) => sum + i.releasedQuantity, 0),
    shipped: items.reduce((sum, i) => sum + i.shippedQuantity, 0),
    canceled: items.reduce((sum, i) => sum + i.canceledQuantity, 0),
});

interface ColumnDeps {
    expandedRows: Set<string>;
    toggleExpanded: (id: string) => void;
    toggleExpandAll: () => void;
    getItemInfo: (asin?: string) => ResellerItem | undefined;
    getPath: (page: SiteMapPage, params?: Record<string, string>) => string;
    totalDemands: number;
    dateFilterField: DateFilterField;
    t: TranslationFunction;
    timezone?: string;
}

const createColumns = ({
    expandedRows,
    toggleExpanded,
    toggleExpandAll,
    getItemInfo,
    getPath,
    totalDemands,
    dateFilterField,
    t,
    timezone,
}: ColumnDeps) => [
    {
        title: (
            <Button type={'icon'} size={'small'} onClick={toggleExpandAll}>
                <Icon tokens={expandedRows.size === totalDemands ? chevronDownSmallTokens : chevronRightSmallTokens}>
                    {t('expandCollapseAll-label')}
                </Icon>
            </Button>
        ),
        uniqueId: 'expand',
        format: (row: SalesOrderDemandRow) =>
            row.isSummary ? (
                <Button type={'icon'} size={'small'} onClick={() => toggleExpanded(row.demandId!)}>
                    <Icon tokens={expandedRows.has(row.demandId!) ? chevronDownSmallTokens : chevronRightSmallTokens}>
                        {t('expandCollapse-label')}
                    </Icon>
                </Button>
            ) : null,
        sortable: false,
    },
    {
        title: t('purchaseOrderId-column'),
        uniqueId: 'purchaseOrderId',
        format: (row: SalesOrderDemandRow) =>
            row.isSummary ? (
                <Link
                    type={'primary'}
                    href={getPath(SiteMapPage.viewPurchaseOrder, { purchaseOrderId: row.purchaseOrderId! })}
                >
                    {t.cognate(row.purchaseOrderId!)}
                </Link>
            ) : null,
    },
    {
        title: t(`${dateFilterField}-column`),
        uniqueId: 'dateColumn',
        format: (row: SalesOrderDemandRow) => {
            if (!row.isSummary) return null;
            const dateFieldMap: Record<DateFilterField, number | undefined> = {
                creationDate: row.creationDate,
                requestedShipDate: row.requestedShipDate,
                requestedDeliveryDate: row.requestedDeliveryDate,
            };
            const dateValue = dateFieldMap[dateFilterField];
            if (!dateValue) return '';
            return formatDateIsoOrBlank(dateValue, timezone);
        },
    },
    {
        title: t('asin-column'),
        uniqueId: 'asin',
        format: (row: SalesOrderDemandRow) => {
            if (row.isSummary) return t('itemsCount-text', { count: row.itemCount });
            return row.item.asin || '';
        },
        sortable: false,
    },
    {
        title: t('retailerSku-column'),
        uniqueId: 'retailerSku',
        format: (row: SalesOrderDemandRow) => {
            if (row.isSummary) return null;
            return getItemInfo(row.item.asin)?.externalSku || '';
        },
        sortable: false,
    },
    {
        title: t('family-column'),
        uniqueId: 'family',
        format: (row: SalesOrderDemandRow) => {
            if (row.isSummary) return null;
            const family = getItemInfo(row.item.asin)?.family || '';
            return family ? t(toFamilyTranslationKey(family), { defaultValue: family }) : '';
        },
        sortable: false,
    },
    {
        title: t('productDescription-column'),
        uniqueId: 'productDescription',
        format: (row: SalesOrderDemandRow) => {
            if (row.isSummary) return null;
            return (
                <TruncatedText noWrap={true} maxWidth={25} text={t.cognate(getItemInfo(row.item.asin)?.title || '')} />
            );
        },
        sortable: false,
    },
    {
        title: t('unitsOrdered-column'),
        uniqueId: 'unitsOrdered',
        format: (row: SalesOrderDemandRow) => String(row.item.orderedQuantity),
    },
    {
        title: t('unitsOpen-column'),
        uniqueId: 'unitsOpen',
        format: (row: SalesOrderDemandRow) => String(row.item.openQuantity),
    },
    {
        title: t('unitsReleased-column'),
        uniqueId: 'unitsReleased',
        format: (row: SalesOrderDemandRow) => String(row.item.releasedQuantity),
    },
    {
        title: t('unitsShipped-column'),
        uniqueId: 'unitsShipped',
        format: (row: SalesOrderDemandRow) => String(row.item.shippedQuantity),
    },
    {
        title: t('unitsCanceled-column'),
        uniqueId: 'unitsCanceled',
        format: (row: SalesOrderDemandRow) => String(row.item.canceledQuantity),
    },
    {
        title: t('shipToLocationCode-column'),
        uniqueId: 'shipToLocationCode',
        format: (row: SalesOrderDemandRow) => {
            if (!row.isSummary) return null;
            return <TruncatedText maxWidth={5} text={t.cognate(row.shipToLocationCode ?? '')} />;
        },
    },
];

export const flattenDemands = (demands: SalesOrderDemand[], expandedRows: Set<string>): SalesOrderDemandRow[] =>
    demands.flatMap((sod) => {
        const isExpanded = expandedRows.has(sod.demandId!);
        const items = sod.items || [];
        const totals = calcTotals(items);

        const summaryRow: SalesOrderDemandRow = {
            ...sod,
            item: {
                orderedQuantity: totals.ordered,
                openQuantity: totals.open,
                releasedQuantity: totals.released,
                shippedQuantity: totals.shipped,
                canceledQuantity: totals.canceled,
            } as SalesOrderDemandItem,
            itemCount: items.length,
            isSummary: true,
        };

        if (!isExpanded) return [summaryRow];

        const itemRows: SalesOrderDemandRow[] = items.map((item, index) => ({
            ...sod,
            item,
            isFirstItem: false,
            isLastItem: index === items.length - 1,
            itemCount: items.length,
            isSummary: false,
        }));

        return [summaryRow, ...itemRows];
    });

export const createTableData = (demands: SalesOrderDemand[], expandedRows: Set<string>, columnDeps: ColumnDeps) => {
    const flattenedData = flattenDemands(demands, expandedRows);
    return new TableData(flattenedData, createColumns(columnDeps));
};
